import { Link } from 'react-router-dom';
import { 
  Phone, 
  ArrowRight, 
  Heart,
  Shield,
  Users,
  MapPin,
  Award,
  Handshake
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function UeberUns() {
  const values = [
    {
      icon: Heart,
      title: 'Menschlichkeit',
      description: 'Jeder Mensch ist einzigartig. Wir behandeln Sie mit Würde, Respekt und echter Zuwendung – so wie wir es uns für unsere eigenen Angehörigen wünschen würden.'
    },
    {
      icon: Shield,
      title: 'Verlässlichkeit',
      description: 'Sie können sich auf uns verlassen. Pünktlich, verbindlich und immer erreichbar. Wir halten, was wir versprechen.'
    },
    {
      icon: Users,
      title: 'Kompetenz',
      description: 'Unser Team besteht aus qualifizierten Pflegefachkräften mit langjähriger Erfahrung. Regelmäßige Fortbildungen sichern unsere hohe Qualität.'
    },
    {
      icon: MapPin,
      title: 'Regionalität',
      description: 'Wir kennen Itzehoe und Umgebung. Als regionaler Partner sind wir schnell bei Ihnen und verstehen die Bedürfnisse unserer Nachbarn.'
    }
  ];

  return (
    <main id="main-content" data-testid="ueber-uns-page">
      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-[#23D3D3] font-semibold text-sm uppercase tracking-wider mb-4">
                Über uns
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-[#1A52D8] leading-tight mb-6">
                Pflege mit Herz – direkt in Itzehoe
              </h1>
              <p className="text-lg md:text-xl text-[#475569] leading-relaxed">
                Das Phönix Pflege Team Itzehoe steht für professionelle ambulante Pflege 
                mit persönlicher Note. Wir sind Ihr verlässlicher Partner für alle 
                Fragen rund um die häusliche Pflege.
              </p>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1765896387377-e293914d1e69?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Freundliche Begegnung zwischen Pflegekraft und Patientin"
                className="w-full h-auto object-cover aspect-[4/3]"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="section-padding bg-white" data-testid="mission-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A52D8] mb-6">
              Wir nehmen uns Zeit für Sie
            </h2>
            <p className="text-lg text-[#475569] leading-relaxed mb-8">
              Gute Pflege braucht Zeit – Zeit zum Zuhören, Zeit zum Verstehen, Zeit für echte Zuwendung. 
              Bei uns stehen Sie als Mensch im Mittelpunkt, nicht als Patient oder Fallnummer.
            </p>
            <p className="text-lg text-[#475569] leading-relaxed">
              Unser Ziel ist es, Ihnen ein selbstbestimmtes Leben in Ihrer vertrauten Umgebung 
              zu ermöglichen – so lange wie möglich und mit der Unterstützung, die Sie brauchen.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#E8F8F8]" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-[#23D3D3] font-semibold text-sm uppercase tracking-wider">
              Unsere Werte
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A52D8] mt-3">
              Wofür wir stehen
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div 
                key={value.title}
                className="bg-white p-8 rounded-2xl shadow-sm"
                data-testid={`value-${index}`}
              >
                <div className="flex items-start gap-5">
                  <div className="icon-container flex-shrink-0">
                    <value.icon className="w-8 h-8 text-[#1A52D8]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#1A52D8] mb-3">{value.title}</h3>
                    <p className="text-[#475569] leading-relaxed">{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cooperation Info */}
      <section className="section-padding bg-white" data-testid="cooperation-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Pflegekraft unterstützt älteres Ehepaar"
                className="w-full h-auto object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="icon-container">
                  <Handshake className="w-8 h-8 text-[#1A52D8]" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#1A52D8]">
                  Unsere Kooperation
                </h2>
              </div>
              
              <p className="text-lg text-[#475569] leading-relaxed mb-6">
                Diese Website wird durch Kommanditisten betrieben, die regional in Itzehoe 
                als Ansprechpartner für Sie da sind.
              </p>
              
              <div className="legal-notice mb-6">
                <p className="text-[#475569] leading-relaxed">
                  <strong className="text-[#1A52D8]">Wichtiger Hinweis:</strong><br />
                  Alle pflegerischen Leistungen werden ausschließlich durch die 
                  <strong>Phönix Pflegeteam Itzehoe</strong> erbracht. Die Phönix Pflegeteam Itzehoe 
                  ist ein zugelassener Pflegedienst und Ihr Vertragspartner für alle Pflegeleistungen.
                </p>
              </div>

              <div className="bg-[#E8F8F8] rounded-2xl p-6">
                <h3 className="font-bold text-[#1A52D8] mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#23D3D3]" />
                  Phönix Pflegeteam Itzehoe
                </h3>
                <p className="text-[#475569] text-sm leading-relaxed">
                  Lindenbusch 13a<br />
                  25578 Dägeling<br />
                  Tel: 0151 46173685<br />
                  E-Mail: info@pflegeteam-itzehoe.de
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regional Focus */}
      <section className="section-padding bg-[#E8F8F8]" data-testid="regional-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center gap-2 bg-[#23D3D3]/20 px-4 py-2 rounded-full mb-6">
              <MapPin className="w-5 h-5 text-[#1A52D8]" />
              <span className="font-semibold text-[#1A52D8]">In Itzehoe zuhause</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A52D8] mb-6">
              Ambulante Pflege in Ihrer Region
            </h2>
            <p className="text-lg text-[#475569] leading-relaxed mb-8">
              Wir betreuen Patienten in Itzehoe, Krempe, Wilster, Glückstadt und der 
              gesamten Umgebung. Als regionaler Pflegedienst kennen wir die Bedürfnisse 
              der Menschen vor Ort und sind schnell bei Ihnen.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Itzehoe', 'Krempe', 'Wilster', 'Glückstadt', 'Kellinghusen', 'Hohenlockstedt'].map((city) => (
                <span 
                  key={city}
                  className="bg-white px-4 py-2 rounded-full text-[#1A52D8] font-medium shadow-sm"
                >
                  {city}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8f8f8] relative overflow-hidden" data-testid="ueber-uns-cta">
        <div className="absolute top-0 left-0 w-64 h-64 bg-[#23D3D3]/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#1A52D8]/5 rounded-full translate-x-1/3 translate-y-1/3" />
        
        <div className="container-custom text-center relative z-10">
          <span className="inline-block text-[#23D3D3] font-semibold text-sm uppercase tracking-wider mb-4">
            Persönlicher Kontakt
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e293b] mb-6">
            Lernen Sie uns kennen
          </h2>
          <p className="text-lg text-[#64748b] mb-10 max-w-xl mx-auto leading-relaxed">
            Wir freuen uns auf ein persönliches Gespräch mit Ihnen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+4915146173685" data-testid="ueber-uns-phone-btn">
              <Button className="bg-[#1A52D8] text-white hover:bg-[#1545b8] h-14 px-10 rounded-full text-lg font-semibold shadow-lg shadow-[#1A52D8]/25 hover:shadow-xl transition-all duration-300">
                <Phone className="w-5 h-5" />
                Jetzt Anrufen
              </Button>
            </a>
            <Link to="/kontakt" data-testid="ueber-uns-contact-btn">
              <Button className="bg-white text-[#1A52D8] border-2 border-[#1A52D8]/20 hover:border-[#1A52D8] h-14 px-10 rounded-full text-lg font-semibold transition-all duration-300">
                Kontakt aufnehmen
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
